import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Status.module.scss';

export type StatusTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

const TONE: Record<StatusTone, string> = {
  neutral: styles['status--neutral'] ?? '',
  accent: styles['status--accent'] ?? '',
  success: styles['status--success'] ?? '',
  warning: styles['status--warning'] ?? '',
  danger: styles['status--danger'] ?? '',
};

export interface StatusProps extends Omit<ComponentPropsWithRef<'span'>, 'color'> {
  tone?: StatusTone;
  /**
   * Действие ещё идёт: точка пульсирует. Для сборки сайта, публикации,
   * загрузки в медиатеку — всего, что закончится само.
   */
  pulse?: boolean;
  /** Подложка вокруг состояния. В плотных таблицах лишняя, в карточках уместна. */
  chip?: boolean;
  /**
   * Подпись обязательна и не имеет значения по умолчанию.
   *
   * Состояние, помеченное только цветом, не читается ни при дальтонизме,
   * ни в чёрно-белой печати, ни в режиме высокой контрастности. Тип не даст
   * отрисовать одну точку без слова.
   */
  children: ReactNode;
}

/**
 * Состояние объекта: черновик, опубликовано, приостановлен, сборка.
 *
 * Отвечает на вопрос «что с этим происходит сейчас», а не «чем это
 * является». Для категорий — тарифов, ролей, тегов — есть `Badge`.
 */
export function Status({
  tone = 'neutral',
  pulse = false,
  chip = false,
  className,
  children,
  ...rest
}: StatusProps) {
  return (
    <span
      className={cx(styles['status'], TONE[tone], chip && styles['status--chip'], className)}
      {...rest}
    >
      <span className={cx(styles['status__dot'], pulse && styles['status__dot--pulse'])} />
      {children}
    </span>
  );
}
